import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs/operators';
import {
  selectBranchId,
  selectPermissions,
  selectUserInfo,
  selectUserStatus,
} from '../../../core/store/auth/auth.selectors';
import { FEEDBACK_READER, MODULE } from '../../../core/services/permission.service';
import { AppointmentRequestService } from '../../../core/services/appointment-request.service';
import { BookingSettingsService } from '../../../core/services/booking-settings.service';
import { ClinicPlanService } from '../../../core/services/clinic-plan.service';
import { SettingsService } from '../../../core/services/settings.service';
import { TranslateService } from '@ngx-translate/core';
import { toast } from 'ngx-sonner';
import { environment } from '../../../../environments/environment';
import * as AuthActions from '../../../core/store/auth/auth.actions';
import { UIStateService } from '../../../core/services/ui-state.service';
import { AuthService } from '../../../core/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'primeng/tooltip';
import { Subject, takeUntil } from 'rxjs';

interface MenuItemConfig {
  text: string;
  icon: string | SafeHtml;
  route: string;
  /** One permission code, or SEVERAL meaning "holding any one shows the entry". Same
   * convention as a route's `data.permission`, and for the same reason: an entry can now
   * lead to a page whose tabs are separate permission modules. */
  permission: string | string[] | null;
  hasSubmenu?: boolean;
  submenuItems?: { text: string; route: string }[];
  hasNews?: boolean;
  // Restricts visibility to a specific auth.selectors `selectUserInfo().user_type_id`
  // (e.g. 2 = admin/clinic-owner) — separate from the permission-string check below.
  usertypeOnly?: number;
  /** Hide this entry until permissions are actually known, instead of showing it during the
   * loading window. `checkPermission` deliberately returns true while `permissions` is null
   * so the menu does not flicker empty on every navigation - but the auth selectors emit
   * their null initial state SYNCHRONOUSLY, so for an entry naming something sensitive that
   * default means every signed-in user reads the label until permissions land. Set this on
   * those entries only; the flicker is the better trade everywhere else. */
  hideUntilPermissionsKnown?: boolean;
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, TranslateModule, TooltipModule],
  templateUrl: './sidebar.html',
  styleUrl: './sidebar.css',
})
export class Sidebar implements OnInit, OnDestroy {
  private store = inject(Store);
  private router = inject(Router);
  private uiState = inject(UIStateService);
  private sanitizer = inject(DomSanitizer);
  private authService = inject(AuthService);
  private appointmentRequests = inject(AppointmentRequestService);
  private bookingSettings = inject(BookingSettingsService);
  private plans = inject(ClinicPlanService);
  private settingsService = inject(SettingsService);
  private translate = inject(TranslateService);

  /**
   * Drives the dot on the Agenda item. A boolean, not a count: the sidebar says
   * "there is something waiting", the Agenda toolbar badge it leads to says how
   * many — repeating the number in both places only gives them a chance to
   * disagree.
   */
  hasPendingRequests = false;

  /** Both halves must agree: the clinic has the CRM, and it wants the link shown. */
  canShowCrmLink = false;
  /** Guards the handoff against a double-click opening two tabs. */
  crmSsoLoading = false;
  private readonly crmAppUrl = (environment.crmAppUrl ?? '').trim().replace(/\/+$/, '');

  isMobileOpen$ = this.uiState.isSidebarMobileOpen$;

  isCollapsed = true;
  activePage = '';
  permissions$ = this.store.select(selectPermissions);
  userStatus$ = this.store.select(selectUserStatus);
  userInfo$ = this.store.select(selectUserInfo);
  clinicType$ = this.authService.clinicType$;
  expandedSubmenu: string | null = null;
  sanitizedMenuItems: MenuItemConfig[] = [];
  visibleMenuItems: MenuItemConfig[] = [];
  private destroy$ = new Subject<void>();

  menuItems: MenuItemConfig[] = [
    {
      text: 'sidebar_dashboard',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /></svg>',
      route: '/dashboard',
      permission: 'Dashboard',
    },
    {
      text: 'sidebar_agenda',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>',
      route: '/schedule',
      permission: 'Agenda',
    },
    {
      text: 'sidebar_consultations',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>',
      route: '/consultations',
      permission: 'Consultas',
    },
    {
      text: 'sidebar_financial',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" /></svg>',
      route: '/financial',
      permission: 'Financeiro',
    },
    {
      text: 'sidebar_forms',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>',
      route: '/forms',
      permission: 'Formulários',
    },
    {
      text: 'sidebar_laboratory',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.183.394l-1.154.908a2 2 0 001.03 3.488h14.504a2 2 0 001.03-3.488h-14.504a2 2 0 001.03-3.488l-1.154-.908zM8.844 4.158a2 2 0 00-2.344 1.688L5.25 10.5m10.156-6.342a2 2 0 012.344 1.688l1.25 4.654M12 10.5v1.5a1.5 1.5 0 11-3 0v-1.5m6 0v1.5a1.5 1.5 0 11-3 0v-1.5m-6 0a1.5 1.5 0 10-3 0v1.5a1.5 1.5 0 103 0v-1.5z" /></svg>',
      route: '/laboratory',
      permission: 'Laboratórios',
    },
    {
      text: 'sidebar_patients',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>',
      route: '/patients',
      permission: 'Pacientes',
    },
    {
      text: 'sidebar_procedures',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>',
      route: '/procedures',
      permission: 'Procedimentos',
    },
    {
      text: 'sidebar_doctors',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>',
      route: '/professionals',
      permission: 'Profissionais',
    },
    {
      text: 'sidebar_rooms',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>',
      route: '/rooms',
      permission: 'Salas',
    },
    {
      // Gated on the module's VIEW flag rather than the bare module name - see the
      // /opinions route in app.routes.ts for why. The owner passes on userStatus 'all'.
      // Hiding an entry is not access control, so the route and the database check too.
      text: 'sidebar_opinions',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>',
      route: '/opinions',
      // EITHER module admits: the page holds one tab per module and a reader holding
      // just one still has a tab to read. Matches the route's own guard data.
      permission: [...FEEDBACK_READER, `${MODULE.COMPLAINTS}:view`],
      hideUntilPermissionsKnown: true,
    },
    {
      text: 'sidebar_statistics',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>',
      route: '/statistics',
      permission: 'Financeiro',
    },
    {
      text: 'sidebar_settings',
      icon: '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>',
      route: '/settings',
      permission: 'Configurações',
    },
  ];

  ngOnInit() {
    // 1. Sanitize initial menu items immediately to show them as a baseline
    this.sanitizedMenuItems = this.menuItems.map((item) => ({
      ...item,
      icon: this.sanitizer.bypassSecurityTrustHtml(item.icon as string),
    }));

    // 2. Default visible items to ALL items immediately (prevents "invisible" sidebar on refresh)
    this.visibleMenuItems = [...this.sanitizedMenuItems];

    // 3. Track active page
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.activePage = event.url;
        this.uiState.closeMobileSidebar(); // Close sidebar on navigation on mobile
      });

    // Sync collapsed state with service
    this.uiState.isSidebarCollapsed$
      .pipe(takeUntil(this.destroy$))
      .subscribe((collapsed) => {
        this.isCollapsed = collapsed;
        if (collapsed) {
          this.expandedSubmenu = null;
        }
      });

    // 4. Filter visible items based on permissions, user status, user type, and clinic type
    combineLatest([this.permissions$, this.userStatus$, this.clinicType$, this.userInfo$]).subscribe(
      ([perms, status, clinicType, userInfo]) => {
        const updatedMenuItems = this.menuItems.map((item) => {
          if (item.route === '/consultations') {
            const iconString =
              clinicType === 'medic'
                ? '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.5 12.5C4.5 16.6421 7.85786 20 12 20C16.1421 20 19.5 16.6421 19.5 12.5C19.5 8.35786 16.1421 5 12 5C7.85786 5 4.5 8.35786 4.5 12.5Z" stroke="currentColor" stroke-width="1.5"/><path d="M8 12.5C8 10.2909 9.79086 8.5 12 8.5C14.2091 8.5 16 10.2909 16 12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M12 5V3M12 20V22M19.5 12.5H21.5M4.5 12.5H2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>'
                : '<svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g clip-path="url(#clip0_8879_278)"><path d="M12 6C10.926 5.414 9.417 4.5 8 4.5C5.9 4.5 4 5.747 4 9.5C4 14.399 5.056 17.91 6.671 20.037C7.244 20.793 8.641 20.558 9.238 19.801C9.636 19.296 10.057 18.362 10.5 17C10.792 16.229 11.392 15.496 12 15.5C12.602 15.5 13.21 16.237 13.5 17C13.943 18.362 14.364 19.295 14.762 19.8C15.359 20.559 16.762 20.793 17.329 20.037C18.944 17.91 20 14.4 20 9.5C20 5.76 18.092 4.5 16 4.5C14.577 4.5 13.08 5.411 12 6Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 6L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></g><defs><clipPath id="clip0_8879_278"><rect width="24" height="24" fill="white" transform="translate(0 0.5)"/></clipPath></defs></svg>';

            return {
              ...item,
              icon: this.sanitizer.bypassSecurityTrustHtml(iconString),
            };
          }
          return {
            ...item,
            icon: this.sanitizer.bypassSecurityTrustHtml(item.icon as string),
          };
        });

        this.visibleMenuItems = updatedMenuItems.filter((item) => {
          // Clinic Type Check: Hide Laboratory for 'medic' specifically
          if (clinicType === 'medic' && item.route === '/laboratory') {
            return false;
          }

          if (!environment.features.feedback && item.route === '/opinions') {
            return false;
          }

          if (item.usertypeOnly !== undefined && userInfo?.user_type_id !== item.usertypeOnly) {
            return false;
          }

          // Before checkPermission, whose loading branch is permissive by design.
          if (item.hideUntilPermissionsKnown && !perms && status !== 'all') {
            return false;
          }

          return this.checkPermission(item.permission, perms, status);
        });
      },
    );

    // 5. Pending-request dot on Agenda. Re-read on every completed navigation
    // as well as on branch change: the sidebar is mounted for the whole session,
    // so a one-off load at boot would still be advertising a queue somebody
    // cleared an hour ago. Two cheap count queries per navigation is the price;
    // the alternative is duplicating the Agenda's realtime subscriptions here.
    //
    // Gated on 'Agenda:create' — the SAME permission as the /schedule/requests
    // route and the Agenda toolbar button that open the queue. Deliberately not
    // create-or-edit: the route guard accepts only create, so a wider rule here
    // would badge the sidebar for someone the destination then bounces. A
    // doctor with plain Agenda access has nothing to do with this queue and
    // was being shown a dot they could not act on.
    const canSeeRequests$ = combineLatest([this.permissions$, this.userStatus$]).pipe(
      map(
        ([perms, status]) =>
          status === 'all' || (perms ?? []).includes('Agenda:create'),
      ),
      distinctUntilChanged(),
    );

    combineLatest([
      canSeeRequests$,
      this.store.select(selectBranchId),
      this.router.events.pipe(
        filter((e) => e instanceof NavigationEnd),
        startWith(null),
      ),
    ])
      .pipe(
        // The count is not merely hidden for users without the permission, it
        // is never requested — there is no reason to tell a doctor's browser
        // how many requests the front desk is sitting on.
        switchMap(([canSee, branchId]) =>
          canSee && branchId
            ? // Never let a failed count break the sidebar — it is the whole
              // app's navigation, and a missing dot is survivable where a
              // broken nav is not.
              this.appointmentRequests.getPendingCount(branchId).pipe(catchError(() => of(0)))
            : of(0),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe((count) => {
        this.hasPendingRequests = count > 0;
      });

    // 6. CRM footer link. Two independent conditions:
    //   - the branch holds a crm.clinic_subscriptions row in trial/active, and
    //   - the company has not switched the link off in Settings → Clinic.
    //
    // Deliberately NOT hung off the NavigationEnd stream above: hasCrmAccess
    // does a fresh round-trip every call (no caching in the service), and a
    // subscription does not change between page views. distinctUntilChanged
    // keeps a repeated branch emission from re-querying for the same answer.
    combineLatest([
      this.store.select(selectBranchId).pipe(
        distinctUntilChanged(),
        switchMap((branchId) =>
          branchId ? this.bookingSettings.hasCrmAccess(branchId) : of(false),
        ),
      ),
      this.userInfo$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([hasCrm, userInfo]) => {
        const info = userInfo as any;
        // Three independent conditions, and they are not the same question:
        //   hasCrm          — the BRANCH holds a subscription
        //   access_crm      — this USER is allowed into the CRM at all
        //   show_crm_link   — the COMPANY wants the shortcut shown
        //
        // access_crm is `=== true` (default-deny, matching the column's own
        // `default false` and the edge function's gate), while show_crm_link
        // is `!== false` because that column defaults to TRUE and older cached
        // userInfo may predate the key entirely — neither of which should hide
        // a link the clinic is paying for.
        this.canShowCrmLink =
          hasCrm && info?.access_crm === true && info?.show_crm_link !== false;
      });
  }

  /**
   * Opens the CRM already signed in, via the crm-sso Edge Function.
   *
   * The tab is opened EMPTY and synchronously, before the request goes out.
   * A window.open() issued later from the response callback has lost the
   * user-gesture context and popup blockers discard it — which looks exactly
   * like the feature being broken. It is parked on about:blank and pointed at
   * the real link once that arrives.
   */
  openCrmPlatform() {
    if (this.crmSsoLoading) return;
    this.crmSsoLoading = true;

    const tab = window.open('', '_blank');

    const hand = (url: string) => {
      if (tab) {
        // Severs the child's window.opener handle to this app. Cannot be done
        // via the 'noopener' feature instead, because that makes window.open
        // return null and there would be no tab left to redirect.
        tab.opener = null;
        tab.location.href = url;
      } else {
        // Popup blocked despite the gesture — better to navigate than to do
        // nothing and leave the click looking dead.
        window.location.href = url;
      }
    };

    this.settingsService
      .getCrmSsoUrl()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ url }) => {
          this.crmSsoLoading = false;
          hand(url);
        },
        error: (err) => {
          this.crmSsoLoading = false;
          // Kept even though the link is now hidden from users without
          // access_crm: that check reads a CACHED userInfo, so a revoked
          // entitlement still shows the link until the cache refreshes. The
          // server is the authority and this is the path it takes when it
          // disagrees — send them to the CRM's own login page rather than
          // reporting an error for something that is simply not allowed.
          if (err?.status === 403) {
            hand(`${this.crmAppUrl}/login`);
            return;
          }
          if (tab) tab.close();
          console.error('CRM handoff failed', err);
          toast.error(this.translate.instant('sidebar_crm_open_failed'));
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleSidebar() {
    const newState = !this.isCollapsed;
    this.uiState.setSidebarCollapsedPersisted(newState);
  }

  closeMobileMenu() {
    this.uiState.closeMobileSidebar();
  }

  toggleSubmenu(itemText: string) {
    if (this.isCollapsed) {
      this.toggleSidebar();
      this.expandedSubmenu = itemText;
      return;
    }
    this.expandedSubmenu = this.expandedSubmenu === itemText ? null : itemText;
  }

  checkPermission(
    permission: string | string[] | null,
    userPermissions: string[] | null,
    userStatus: string | null,
  ): boolean {
    if (!permission) return true;
    // Blank entries dropped, so an empty string keeps meaning "no permission required".
    const required = (Array.isArray(permission) ? permission : [permission]).filter(Boolean);
    if (!required.length) return true;
    // The clinic's plan first: a module it leaves out is hidden from owners too.
    if (!required.some((code) => this.plans.allows(code))) return false;
    if (userStatus === 'all') return true;
    if (!userPermissions) return true; // Show all while permissions are loading
    return required.some((code) => userPermissions.includes(code));
  }

  getUserInitials(name: string | null): string {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}
